import React, {createRef, useEffect, useState} from "react";
import '../../styles/components/lesson/soundslice-iframe.scss';
import {subscribeToSendMessageToSoundsliceIframeEvents} from "../../services/eventBus/soundslice/soundSliceSdk";
import {
  IDrawTempNote,
  IEditNote,
  ISetLoop,
  ISetTrackVisibility,
  SoundsliceRequestEventTypes
} from "../../services/eventBus/soundslice/SoundsliceRequestEventTypes";
import LoadingAnimation2 from "../loadingAnimation/LoadingAnimation2";
import {
  subscribeToReceiveMessageFromSoundsliceIframeEvents
} from "../../services/eventBus/soundslice/receiveMessageFromSoundsliceIframeEventBus";
import {logger} from "../../services/log/logger";
import {useAppSelector} from "../../redux/hooks";
import {lessonPlayerStore} from "../../redux/lesson/LessonPlayerStore";

interface ISoundsliceIframeProps {
  onLoad?: () => void;
}

/**
 * Waits for the iframe to load before displaying it, in order to avoid 1) jerkiness 2) Soundslice control bar briefly flashes.
 * The iframe uses a unique key for each render, so that the iframe component has all style/etc reset with each render.
 * We wait for the iframe load event, and then add an "iframe-loaded" class to the element.
 * @param iframeUrl
 * @param onLoad
 * @constructor
 */
export default function SoundsliceIframe({onLoad,}: ISoundsliceIframeProps) {
  const segment = useAppSelector(lessonPlayerStore.getSegment);
  const iframeUrl = segment?.soundsliceIframeUrl ?? '';
  const iframeRef = createRef<HTMLIFrameElement>();
  useEffect(()=> subscribeToSendMessageToSoundsliceIframeEvents((event: SoundsliceRequestEventTypes)=>{
    sendMessageToIframe(event, iframeRef.current || document.querySelector(`#ssIframe`)); //weird new issue where the iframeRef is null sometimes.  Use query selector as backup.
  }));

  //reset iframe loading class to loading anytime the url changes so that the animation is displayed between segments.
  useEffect(()=> {
    setIframeLoadingClass("loading");
    return subscribeToReceiveMessageFromSoundsliceIframeEvents((event)=>{
      if(event.type === "ssPlayerReady"){
        setIframeLoadingClass("done-loading");
        setTimeout(()=> setIframeLoadingClass(""), 2000);
      }
    });
  }, [iframeUrl]);

  const [iframeLoadingClass, setIframeLoadingClass] = useState("loading");

  return <div className={`soundslice-iframe ${iframeLoadingClass}`}>
    <LoadingAnimation2 additionalClassName={""}/>
    <iframe key={iframeUrl} id="ssIframe" src={iframeUrl} onLoad={()=> {
      if(iframeRef.current) {
        iframeRef.current.classList.add(`iframe-loaded`);
      }
      if(onLoad) {
        onLoad();
      }

      // The allowFullScreen attribute is deprecated in HTML5.  The allow attribute is the new way to do this.
      // As of 4/9/2025 Safari doesn't support the new "allow" syntax. Have to keep allowFullScreen attribute in until safari supports it.
      // Leaving allowFullScreen in produces this warning "Allow attribute will take precedence over 'allowfullscreen'." which is annoying.
    } } ref={iframeRef} allow="autoplay; fullscreen" allowFullScreen/>
  </div>;
}

function sendMessageToIframe(message: SoundsliceRequestEventTypes, iframe: HTMLIFrameElement | null){
  if (!iframe || !iframe.contentWindow) {
    return logger.vendorSoundSliceMessageSend.error({message: `${Date.now()} soundslice iframe is not ready to receive post messages` + iframe});
  }

  const soundsliceMessage = convertSoundsliceEventToSoundsliceMethodFormat(message);
  const soundsliceMessageAsString = JSON.stringify(soundsliceMessage);

  if (message.type !== "editNote") {
    logger.vendorSoundSliceMessageSend.debug({message: `sending message to iframe: ${soundsliceMessageAsString}`});
  }

  iframe.contentWindow.postMessage(soundsliceMessageAsString, "https://www.soundslice.com");
}

/**
 * Converts our representation of Soundslice events/messages to the format their iframe expects.
 * Since there should only ever be 1 or 2 properties (method and method arg), we can avoid using switch statement to pluck out the arg value and use object spread to help us obtain the arg value.
 * @param event - represents which message to post to the iframe, and what (optional) arg to send.  object with 1 or 2 properties: type and whatever value to be sent.
 */
function convertSoundsliceEventToSoundsliceMethodFormat(event: SoundsliceRequestEventTypes){
  if(event.type === "setTrackVisibility"){ return convertSetTrackVisibilitySoundsliceEventToSoundsliceMethodFormat(event); }
  if(event.type === "setLoop"){ return convertSetLoopSoundsliceEventToSoundsliceMethodFormat(event); }
  if(event.type === "editNote") { return convertEditNoteSoundsliceEventToSoundsliceMethodFormat(event); }
  if(event.type === "drawTempNote") { return convertDrawTempNoteSoundsliceEventToSoundsliceMethodFormat(event); }
  return convertTypicalSoundsliceEventToSoundsliceMethodFormat(event);
}

function convertEditNoteSoundsliceEventToSoundsliceMethodFormat(event: IEditNote){
  const {color, trackIndex, barIndex, noteIndex, beatIndex, voiceIndex, type: method, state} = event;
  const note = [barIndex, trackIndex, voiceIndex, beatIndex, noteIndex];
  return {method, note, color, state};
}

function convertDrawTempNoteSoundsliceEventToSoundsliceMethodFormat(event: IDrawTempNote){
  const {barOffsetPercent, midi, barIndex, color, trackIndex, type: method, state} = event;
  const arg = [barIndex, trackIndex, midi, barOffsetPercent, color];
  return {method, arg, state};
}
/**
 * All of soundslice messages work with this function, except for setTrackVisibility.
 * @param event
 */
function convertTypicalSoundsliceEventToSoundsliceMethodFormat(event: SoundsliceRequestEventTypes){
  const {type: method, state, ...rest} = event; //grab the event "type" as var "method".  Move remaining props into an object named "rest".
  const arg = event[Object.keys(rest)[0] as keyof typeof event]; //grab the value of the first entry in the rest obj and assign it to arg.
  return {method, arg, state};
}

/**
 * SetTrackVisibility is a special case due to the argument format ss uses.
 * {"method": "setTrackVisibility", "track": 0, "type": 7, "arg": 0}
 * @param event
 */
function convertSetTrackVisibilitySoundsliceEventToSoundsliceMethodFormat(event: ISetTrackVisibility){
  const {type: method, visibilityType: type, isVisible, state} = event; //grab the event "type" as var "method".  Move remaining props into an object named "rest".
  const arg = Number(isVisible);
  return {method, type, arg, state};
}

function convertSetLoopSoundsliceEventToSoundsliceMethodFormat(event: ISetLoop){
  const {type: method, startTimeSeconds, endTimeSeconds, state} = event;
  const arg = [startTimeSeconds, endTimeSeconds];
  return {method, arg, state}
}